import { useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import { aboutImage, comics } from "@/mocks/comics";

const principles = [
  { icon: "ri-star-line", title: "只推好作品", desc: "坚持品质优先，不追流量、不注水，每一部上架作品都经过内容团队把关。" },
  { icon: "ri-archive-drawer-line", title: "内容有分类", desc: "科幻、恋爱、奇幻、悬疑、治愈……按题材分类整理，让你快速找到想要的那类漫画。" },
  { icon: "ri-refresh-line", title: "每日在刷新", desc: "今日更新内容池每天刷新，第一时间呈现最新上架与连载，不错过任何一部想追的作品。" },
  { icon: "ri-chat-heart-line", title: "倾听读者", desc: "读者想看什么，我们就去找什么。欢迎通过联系方式告诉我们你的追更心愿。" },
];

const values = [
  { num: "12", label: "精选分类" },
  { num: "100+", label: "口碑作品" },
  { num: "500万+", label: "累计人气" },
  { num: "365", label: "每日更新" },
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const schema = {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "关于卡通动漫第一页",
      description: "卡通动漫第一页是一个聚合今日更新、热门榜单与编辑推荐的动漫漫画内容平台。",
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schema);
    script.id = "about-ld";
    document.getElementById("about-ld")?.remove();
    document.head.appendChild(script);
    return () => {
      document.getElementById("about-ld")?.remove();
    };
  }, []);

  return (
    <>
      <Seo
        title="关于我们 - 卡通动漫第一页"
        description="了解卡通动漫第一页：我们聚合今日更新、热门榜单与编辑推荐，坚持只推好作品、每日刷新内容池，帮你快速找到想追的那部漫画。"
        keywords="卡通动漫第一页,关于我们,平台介绍,内容原则"
      />
      <Navbar />

      <main className="bg-background-50 pt-16 md:pt-20">
        <div className="container-x">
          <nav className="flex items-center gap-2 py-6 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-700 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line" style={{ fontSize: "16px" }}></i>
            <span className="text-foreground-800">关于我们</span>
          </nav>

          {/* 品牌介绍 */}
          <section className="flex flex-col items-center gap-8 py-6 lg:flex-row lg:gap-12" aria-label="品牌介绍">
            <div className="flex-1">
              <h1 className="font-display text-3xl text-foreground-950 md:text-4xl">关于卡通动漫第一页</h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                卡通动漫第一页是一个面向动漫爱好者的内容聚合平台。我们把「今日更新」「热门榜单」「编辑推荐」与「人气连载」汇聚到一页之中，让每一位读者都能在最短的时间里，找到当下最值得追的那部作品。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                我们相信，好的内容值得被更多人看见。无论是口碑爆棚的高分连载，还是被埋没的冷门佳作，只要故事足够动人、画风足够用心，都值得出现在这个页面上。
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/today" className="rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium whitespace-nowrap text-background-50 hover:bg-primary-600 transition-colors">查看今日更新</Link>
                <Link to="/contact" className="rounded-full bg-background-100 px-6 py-2.5 text-sm font-medium whitespace-nowrap text-foreground-800 ring-1 ring-background-200 hover:bg-background-200 transition-colors">联系我们</Link>
              </div>
            </div>
            <div className="w-full max-w-md flex-shrink-0 overflow-hidden rounded-2xl ring-1 ring-background-200">
              <img src={aboutImage} alt="卡通动漫第一页编辑团队工作场景插画" title="关于卡通动漫第一页编辑团队" className="h-full w-full object-cover" />
            </div>
          </section>

          {/* 数据 */}
          <section className="grid grid-cols-2 gap-4 py-8 sm:grid-cols-4" aria-label="平台数据">
            {values.map((v) => (
              <div key={v.label} className="rounded-xl bg-background-100 p-5 text-center ring-1 ring-background-200">
                <div className="font-display text-3xl text-primary-600">{v.num}</div>
                <div className="mt-1 text-sm text-foreground-500">{v.label}</div>
              </div>
            ))}
          </section>

          {/* 发展理念 */}
          <section className="py-8" aria-label="发展理念">
            <h2 className="mb-5 flex items-center gap-2 font-display text-2xl text-foreground-950">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-500 text-foreground-950"><i className="ri-compass-line" style={{ fontSize: "18px" }}></i></span>
              我们的发展理念
            </h2>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {principles.map((p, i) => (
                <div key={p.title} className="rounded-xl bg-background-100 p-5 ring-1 ring-background-200">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-500 text-xl text-background-50">
                    <i className={p.icon} style={{ fontSize: "22px" }}></i>
                  </span>
                  <div className="mt-4 text-xs text-foreground-400">0{i + 1}</div>
                  <h3 className="mt-1 font-medium text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* 内容原则 */}
          <section className="rounded-2xl bg-primary-950 p-6 py-10 text-background-100 md:p-10" aria-label="内容原则">
            <h2 className="font-display text-2xl text-background-50 md:text-3xl">我们坚持的内容原则</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="rounded-xl bg-white/5 p-5 ring-1 ring-white/10">
                <h3 className="flex items-center gap-2 font-medium text-background-50"><i className="ri-focus-3-line" style={{ fontSize: "18px" }}></i> 品质优先</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-100/80">评分与推荐以真实阅读体验为准，不追热度、不注水，坚持把更好的作品放在更靠前的位置。</p>
              </div>
              <div className="rounded-xl bg-white/5 p-5 ring-1 ring-white/10">
                <h3 className="flex items-center gap-2 font-medium text-background-50"><i className="ri-shield-check-line" style={{ fontSize: "18px" }}></i> 尊重版权</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-100/80">我们尊重每一位创作者，内容仅作展示与交流，全部作品版权归原作者所有。</p>
              </div>
              <div className="rounded-xl bg-white/5 p-5 ring-1 ring-white/10">
                <h3 className="flex items-center gap-2 font-medium text-background-50"><i className="ri-plant-line" style={{ fontSize: "18px" }}></i> 题材多元</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-100/80">科幻、恋爱、悬疑、治愈、古风……我们愿意让不同类型的作品都有被看见的机会。</p>
              </div>
              <div className="rounded-xl bg-white/5 p-5 ring-1 ring-white/10">
                <h3 className="flex items-center gap-2 font-medium text-background-50"><i className="ri-message-3-line" style={{ fontSize: "18px" }}></i> 倾听反馈</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-100/80">内容池的扩充离不开读者，你的每一次建议都会成为我们优化方向的参考。</p>
              </div>
            </div>
          </section>

          {/* 编辑之选口碑 */}
          <section className="py-10" aria-label="编辑精选">
            <h2 className="mb-5 font-display text-2xl text-foreground-950">编辑正在追的作品</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {comics.slice(0, 4).map((c) => (
                <Link
                  key={c.id}
                  to={`/comic/${c.id}`}
                  className="group flex flex-col overflow-hidden rounded-xl bg-background-100 ring-1 ring-background-200 transition-transform duration-300 hover:-translate-y-1 hover:ring-primary-300"
                  aria-label={`查看《${c.title}》`}
                >
                  <div className="relative aspect-[3/4] w-full overflow-hidden">
                    <img src={c.cover} alt={`编辑之选《${c.title}》封面`} title={`${c.title} 编辑之选`} className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    <span className="absolute left-2 top-2 rounded-full bg-accent-500 px-2.5 py-1 text-xs font-medium text-foreground-950">编辑之选</span>
                  </div>
                  <div className="p-3">
                    <h3 className="truncate font-medium text-foreground-950 group-hover:text-primary-700 transition-colors">{c.title}</h3>
                    <p className="mt-1 text-xs text-foreground-500">{c.updates}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </>
  );
}